import { Flex, Button, Text, Badge } from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useContext, useState } from "react";
import { getCustomButtonVariant } from "utils/uiconfighelper";
import { SectionViewModel } from "./section.view-model";
import { apiRequest } from "utils/api";
import { getErrorMessage } from "utils/errorHandler";
import { AlertsContext } from "providers/alerts";
import { Reorder } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";

interface SectionsReorderProps {
  sections: SectionViewModel[];
  onCancel: () => void;
  onSaveChanges: (sections: SectionViewModel[]) => void;
}

export function SectionsReorder({ sections, onCancel, onSaveChanges }: SectionsReorderProps) {
  const { user } = useTenant();
  const { showSuccess, showError } = useContext(AlertsContext);
  const queryClient = useQueryClient();

  const [newSectionOrder, setNewSectionOrder] = useState<SectionViewModel[]>(sections);

  async function handleSaveChanges() {
    try {
      await apiRequest("/supabase/loan_section/reorder", {
        fields: newSectionOrder,
      });

      showSuccess({
        title: "Order Saved",
        description: "The order of your sections has been saved.",
      });

      // Invalidate the loanSection query to refresh data in other components
      queryClient.invalidateQueries({ queryKey: ["loanSection"] });

      // Also invalidate the loan query to ensure the OverviewTab gets updated
      queryClient.invalidateQueries({ queryKey: ["/supabase/loans/single"] });

      onSaveChanges(newSectionOrder);
    } catch (err) {
      showError({
        title: "Error saving order",
        description: getErrorMessage(err),
      });
    }
  }

  function isHiddenFromBorrowers(section: SectionViewModel) {
    return section.fields.every(
      field => !field.show_on_borrower_application && !field.show_on_borrower_loan
    );
  }

  return (
    <Flex direction="column" gap={4}>
      <Flex direction="row" gap={3}>
        <Button
          variant="outline"
          leftIcon={<FontAwesomeIcon icon={["far", "xmark"]} />}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          variant={getCustomButtonVariant(user)}
          leftIcon={<FontAwesomeIcon icon={["far", "save"]} />}
          onClick={handleSaveChanges}
        >
          Save Order
        </Button>
      </Flex>

      <Reorder.Group
        values={newSectionOrder}
        onReorder={setNewSectionOrder}
        style={{
          listStyleType: "none",
          padding: "0px",
          margin: "0px",
          width: "100%",
        }}
      >
        {newSectionOrder.map((section: SectionViewModel) => {
          return (
            <Reorder.Item
              value={section}
              key={section.id}
              style={{
                padding: "12px",
                borderWidth: "1px",
                borderColor: "#DEDCE2",
                marginBottom: "8px",
                borderRadius: "8px",
                cursor: "grab",
              }}
            >
              <Flex alignItems="center" justifyContent="space-between">
                <Flex width="100%" direction="row" alignItems="center" gap={3}>
                  <FontAwesomeIcon icon={["far", "bars"]} />
                  <Text as="span" flex="1" fontWeight="semibold" color="black">
                    {section.section_name}
                  </Text>
                  {isHiddenFromBorrowers(section) && (
                    <Badge size="sm" variant="outline" fontSize="12px" bg="white" color="gray.600">
                      Hidden from Borrowers
                    </Badge>
                  )}
                </Flex>
              </Flex>
            </Reorder.Item>
          );
        })}
      </Reorder.Group>
    </Flex>
  );
}
